import axios from 'axios';

import Storage from 'storage';
import {
  APISpecialtyForm,
  APILifeInsuranceForm,
  APIBaraReferral,
  APIBaraReferralPage,
} from 'types/api.types';

import { POST, GET, PATCH, DELETE } from 'utils/api';
import { VALIDATION_TYPE_LEGACY_AUTO } from 'variables/constants';

const apiAuthenticate = (data: { email: string; password: string }) => {
  return POST('api/organization/signin', data);
};

const apiSignOut = () => {
  return POST('api/organization/signout', {});
};

const apiApplicationFormData = (uuid: string) => {
  return GET(`api/policy/mc/${uuid}/application/form`);
};

const apiCreateApplicationLog = (
  uuid: string,
  data: any,
  remarks: any,
  files: any,
  isFormComplete?: boolean,
) => {
  return POST(`api/policy/mc/${uuid}/application/log`, {
    data,
    remarks,
    files,
    is_form_complete: isFormComplete,
  });
};

const apiValidations = async (
  uuid: string,
  auth?: CustomAuth,
  submissionId?: number,
) => {
  const token = auth?.token || Storage.get('user')?.token;
  const endpoint = auth?.endpoint || Storage.get('API_ENDPOINT');
  const url = submissionId
    ? `api/policy/mc/${uuid}/${submissionId}`
    : `api/policy/mc/${uuid}`;

  try {
    const fullResponse = await axios.get(endpoint + url, {
      headers: {
        ...(auth ? { Token: token } : { User: token }),
      },
    });

    if (fullResponse.status === 202) {
      return {
        status: 202,
        data: fullResponse.data,
      };
    }

    return fullResponse.data;
  } catch (error: any) {
    throw {
      status: error.response.status,
      error: error.response.data.error,
    };
  }
};

const apiCreateApplicationFile = (uuid: string, data: FormData) => {
  return POST(`api/policy/mc/${uuid}/application/file`, data);
};

const apiUploadFile = (uuid: string | null, data: any) => {
  if (uuid) {
    return POST(`api/policy/upload/${uuid}`, data);
  }

  return POST('api/policy/upload', data);
};

const apiPolicyList = (page: number, date_from: string, date_to: string) => {
  return GET(
    `api/policy/mc/list${
      page > 0 ? `?page=${page}` : ''
    }&date_from=${date_from}&date_to=${date_to}`,
  );
};

const apiPolicyOverview = (uuid: string) => {
  return GET(`api/policy/mc/${uuid}/overview`);
};

const apiCreateNewUser = (data: any) => {
  return POST('api/organization/users', data);
};

const apiGetUsers = (page: number, query: string) => {
  const params = page > 0 ? `?page=${page}&${query}` : `?${query}`;
  return GET(`api/organization/users${params}`);
};

const apiGetUser = (user_id: number) => {
  return GET(`api/organization/users/${user_id}`);
};

const apiUpdateUser = (user_id: number, data: any) => {
  return PATCH(`api/organization/users/${user_id}`, data);
};

const apiResyncActurisConfig = (user_id: number) => {
  return POST(`api/organization/users/${user_id}/resync-acturis`, {});
};

const apiRemoveUser = (user_id: number) => {
  return DELETE(`api/organization/users/${user_id}`);
};

const apiSetOnlineStatus = (user_id: number, is_online: boolean) => {
  return POST(`api/organization/users/${user_id}/online`, {
    is_online: is_online,
  });
};

const apiAnalytics = (from: string, to: string) => {
  return GET(`api/policy/mc/analytics?from=${from}&to=${to}`);
};

const apiAativeLeadList = (
  page: number,
  query: string,
  viewAsEmail?: string,
  viewAsBranchId?: string,
) => {
  let params = page > 0 ? `?page=${page}&${query}` : `?${query}`;
  if (viewAsEmail)
    params += `&view_as_email=${encodeURIComponent(viewAsEmail)}`;
  else if (viewAsBranchId)
    params += `&view_as_branch_id=${encodeURIComponent(viewAsBranchId)}`;
  return GET(`api/leads/active${params}`);
};

const apiUnassignedLeadList = (
  page: number,
  query: string,
  viewAsEmail?: string,
  viewAsBranchId?: string,
) => {
  let params = page > 0 ? `?page=${page}&${query}` : `?${query}`;
  if (viewAsEmail)
    params += `&view_as_email=${encodeURIComponent(viewAsEmail)}`;
  else if (viewAsBranchId)
    params += `&view_as_branch_id=${encodeURIComponent(viewAsBranchId)}`;
  return GET(`api/leads/unassigned${params}`);
};

const apiAllLeadList = (
  page: number,
  query: string,
  viewAsEmail?: string,
  viewAsBranchId?: string,
) => {
  let params = page > 0 ? `?page=${page}&${query}` : `?${query}`;
  if (viewAsEmail)
    params += `&view_as_email=${encodeURIComponent(viewAsEmail)}`;
  else if (viewAsBranchId)
    params += `&view_as_branch_id=${encodeURIComponent(viewAsBranchId)}`;
  return GET(`api/leads/all${params}`);
};

const apiLeadOverview = (
  id: number,
  viewAsEmail?: string,
  viewAsBranchId?: string,
) => {
  let params = '';
  if (viewAsEmail) params = `?view_as_email=${encodeURIComponent(viewAsEmail)}`;
  else if (viewAsBranchId)
    params = `?view_as_branch_id=${encodeURIComponent(viewAsBranchId)}`;
  return GET(`api/leads/${id}/overview${params}`);
};

const apiLeadStatusUpdate = (id: number, data: any) => {
  return PATCH(`api/leads/${id}/status`, data);
};

const apiCreateApplicationFileValidatorLog = (uuid: string, data: any) => {
  return POST(`api/policy/mc/${uuid}/application/file-validator-log`, data);
};

const apiApplicationFileValidatorLog = (uuid: string, id: number) => {
  return GET(`api/policy/mc/${uuid}/application/file-validator-log/${id}`);
};

const apiBrokerUploadDocument = (
  uuid: string,
  validation_key: string,
  item_key: string,
  file: File,
  type: number,
) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('validation', validation_key);
  formData.append('item_key', item_key);
  formData.append('type', type.toString());
  return POST(`api/policy/mc/documents/${uuid}/upload`, formData);
};

const apiPublicUploadDocument = (
  signed_uuid: string,
  validation_key: string,
  item_key: string,
  file: File,
  type: number,
) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('validation', validation_key);
  formData.append('item_key', item_key);
  formData.append('type', type.toString());
  return POST(`api/policy/mc/public/${signed_uuid}/documents`, formData);
};

const apiSendDocumentReminder = (uuid: string) => {
  return POST(`api/policy/mc/documents/${uuid}/reminder`, {});
};

const apiUpdateDocumentStatus = (
  uuid: string,
  validation_key: string,
  item_key: string,
  status: 'resolved' | 'cannot_obtain',
) => {
  return POST(`api/policy/mc/documents/${uuid}/manage`, {
    validation_key,
    item_key,
    status,
  });
};

const apiResolveDocument = (
  uuid: string,
  validation_key: string,
  item_key: string,
) => {
  return apiUpdateDocumentStatus(uuid, validation_key, item_key, 'resolved');
};

const apiIgnoreDocument = (
  uuid: string,
  validation_key: string,
  item_key: string,
) => {
  return apiUpdateDocumentStatus(
    uuid,
    validation_key,
    item_key,
    'cannot_obtain',
  );
};

const apiDeleteUploadedFile = (
  uuid: string,
  validation_key: string,
  item_key: string,
  file_id: number,
) => {
  return DELETE(`api/policy/mc/documents/${uuid}/manage`, {
    validation_key,
    item_key,
    file_id,
  });
};

const apiGetDeferredDocuments = (
  viewAsEmail?: string,
  viewAsBranchId?: string,
) => {
  const params = new URLSearchParams();
  if (viewAsEmail) params.append('view_as_email', viewAsEmail);
  if (viewAsBranchId) params.append('view_as_branch_id', viewAsBranchId);
  const qs = params.toString();
  return GET(qs ? `api/policy/mc/documents?${qs}` : 'api/policy/mc/documents');
};

const apiSearch = (query: string) => {
  return GET(`api/policy/mc/search?q=${query}`);
};

const apiGetPublicDeferredDocuments = (uuid: string) => {
  return GET(`api/policy/mc/public/${uuid}/documents`);
};

const apiAssignLeadToUser = (leadId: number, userId: number | 'me') => {
  return POST(`api/leads/${leadId}/assign/${userId}`, null);
};

const apiLeadFileUpload = (leadId: number, data: any) => {
  return POST(`api/leads/${leadId}/upload`, data);
};

const apiUpdateLead = (id: number, data: any) => {
  return PATCH(`api/leads/${id}/overview`, data);
};

const apiCreateLead = (data: any) => {
  return POST('api/leads/', data);
};

const apiGetLeadSources = () => {
  return GET('api/leads/sources');
};

const apiGetUserWorkflows = () => {
  return GET('api/leads/workflows/user');
};

const apiGetWorkflowUsers = (workflow_id: number) => {
  return GET(`api/leads/workflow/${workflow_id}/users`);
};

const apiGetLeadTags = (page: number, query: string) => {
  const params = page > 0 ? `?page=${page}&${query}` : `?${query}`;
  return GET(`api/leads/tags${params}`);
};

const apiGetLeadTag = (tag_id: number) => {
  return GET(`api/leads/tags/${tag_id}`);
};

const apiUpdateLeadTag = (tag_id: number, data: any) => {
  return PATCH(`api/leads/tags/${tag_id}`, data);
};

const apiGetLeadLogs = (lead_id: string) => {
  return GET(`api/leads/${lead_id}/log`);
};

const apiGetBinderReportData = (uuid: string, submissionId?: number) => {
  const params = submissionId ? `?submission_id=${submissionId}` : '';
  return GET(`api/policy/mc/${uuid}/binder-report${params}`);
};

const apiGetBinderChecklist = (uuid: string) => {
  return GET(`api/policy/mc/${uuid}/binder-checklist`);
};

const apiUpdateBinderChecklist = (
  uuid: string,
  checklistData: { [validationId: string]: { checked: boolean } },
) => {
  return POST(`api/policy/mc/${uuid}/binder-checklist`, {
    validations: checklistData,
  });
};

const apiCreateLeadTag = (data: any) => {
  return POST('api/leads/tags', data);
};

const apiGetBranches = (page: number, query: string) => {
  const params = page > 0 ? `?page=${page}&${query}` : `?${query}`;
  return GET(`api/organization/branches${params}`);
};

const apiCreateBranch = (data: any) => {
  return POST('api/organization/branches', data);
};

const apiGetBranch = (branch_id: number) => {
  return GET(`api/organization/branches/${branch_id}`);
};

const apiUpdateBranch = (branch_id: number, data: any) => {
  return PATCH(`api/organization/branches/${branch_id}`, data);
};

const apiGetMyBranches = (page: number, query: string = '') => {
  const params = page > 0 ? `?page=${page}&${query}` : `?${query}`;
  return GET(`api/organization/branches/me${params}`);
};

const apiGetSupervisorCandidates = (policyUuid?: string) => {
  const params = policyUuid ? `?policy=${policyUuid}` : '';
  return GET(`api/organization/supervisor-candidates${params}`);
};

const apiGetActurisConfigStatus = () => {
  return GET('api/organization/broker/acturis-config-status');
};

const apiResyncBrokerActurisConfig = () => {
  return POST('api/organization/broker/resync-acturis', {});
};

const apiGetWorkflows = (page: number, query: string) => {
  const params = page > 0 ? `?page=${page}&${query}` : `?${query}`;
  return GET(`api/leads/workflows${params}`);
};

const apiGetWorkflowLeadSources = () => {
  return GET('api/leads/workflows/sources');
};

const apiCreateWorkflow = (data: any) => {
  return POST('api/leads/workflows', data);
};

const apiUpdateWorkflow = (id: number, data: any) => {
  return PATCH(`api/leads/workflow/${id}`, data);
};

const apiGetWorkflow = (workflow_id: number) => {
  return GET(`api/leads/workflow/${workflow_id}`);
};

const apiAddUserToWorkflow = (
  workflow_id: number,
  user_id: number,
  data: any,
) => {
  return POST(`api/leads/workflow/${workflow_id}/user/${user_id}`, data);
};

const apiRemoveUserFromWorkflow = (workflow_id: number, user_id: number) => {
  return DELETE(`api/leads/workflow/${workflow_id}/user/${user_id}`);
};

const apiGetCallList = (
  page: number,
  sort?: { sort_field?: string; sort_direction?: string },
) => {
  const params = new URLSearchParams();

  if (page > 0) {
    params.append('page', page.toString());
  }

  Object.entries(sort ?? {}).forEach(([key, value]) => {
    if (value) {
      params.append(key, value);
    }
  });

  const query = params.toString();

  return GET(`api/call_list/${query ? `?${query}` : ''}`);
};

const apiUpdateCallListItem = (
  id: number,
  data: {
    notes?: string;
    status?: number;
    effective_date?: string;
    lead?: number;
    user?: number;
  },
) => {
  return PATCH(`api/call_list/${id}`, data);
};

const apiExcludeCallListItem = (id: number) => {
  return GET(`api/call_list/${id}/exclude`);
};

const apiGetCallListItemLeads = (id: number) => {
  return GET(`api/call_list/${id}/leads`);
};

const apiGetCallListUsers = () => {
  return GET('api/call_list/users');
};

const apiBulkAssignCallListItems = (data: {
  items: number[];
  user: number;
}) => {
  return POST('api/call_list/bulk', data);
};

const apiGetCallListAnalytics = () => {
  return GET('api/call_list/analytics');
};

const apiGetSalesAnalytics = (params: {
  year_month?: string;
  period?: string;
}) => {
  const qs = new URLSearchParams(
    Object.fromEntries(
      Object.entries(params).filter(([, v]) => v != null),
    ) as Record<string, string>,
  );
  return GET(`api/analytics/sales-analytics?${qs}`);
};

const apiGetAnalyticsCallList = (params: {
  year_month: string;
  period: string;
  branch_id?: number | null;
  broker_email?: string | null;
}) => {
  const qs = new URLSearchParams(
    Object.fromEntries(
      Object.entries(params).filter(([, v]) => v != null),
    ) as Record<string, string>,
  );
  return GET(`api/analytics/call-list?${qs.toString()}`);
};

const apiGetDrillDown = (params: {
  type: 'branch' | 'broker';
  id: string;
  month?: string;
}) => {
  const qs = new URLSearchParams(
    Object.fromEntries(
      Object.entries(params).filter(([, v]) => v != null),
    ) as Record<string, string>,
  );
  return GET(`api/analytics/drill-down?${qs}`);
};

const apiGetLeadManagement = (params: { year_month?: string }) => {
  const qs = new URLSearchParams(
    Object.fromEntries(
      Object.entries(params).filter(([, v]) => v != null),
    ) as Record<string, string>,
  );
  const query = qs.toString();
  return GET(`api/analytics/lead-management${query ? `?${query}` : ''}`);
};

const apiGetAnalyticsSubmissions = (params: {
  year_month?: string;
  lob?: string;
  pol_type?: string;
  market?: string;
  state?: string;
  source?: string;
}) => {
  const qs = new URLSearchParams(
    Object.fromEntries(
      Object.entries(params).filter(([, v]) => v != null),
    ) as Record<string, string>,
  );
  const query = qs.toString();
  return GET(`api/analytics/submissions${query ? `?${query}` : ''}`);
};

const apiGetAnalyticsOverview = (params: {
  year_month?: string;
  branch_id?: number | null;
  broker_email?: string | null;
  period?: string;
  market?: string | null;
  business_type?: string | null;
}) => {
  const qs = new URLSearchParams(
    Object.fromEntries(
      Object.entries(params).filter(([, v]) => v != null),
    ) as Record<string, string>,
  );
  const query = qs.toString();
  return GET(`api/analytics/overview${query ? `?${query}` : ''}`);
};

const apiGetLeadHandlers = () => {
  return GET('api/leads/handlers');
};

const apiGetCarriers = (page: number, query: string, runner_type?: string) => {
  let params = page > 0 ? `?page=${page}&${query}` : `?${query}`;
  if (runner_type) {
    params += `${params.includes('?') ? '&' : '?'}runner_type=${runner_type}`;
  }
  return GET(`api/carriers/${params}`);
};

const apiCreateCarrier = (data: any) => {
  return POST('api/carriers/', data);
};

const apiGetCarrier = (carrier_id: number) => {
  return GET(`api/carriers/${carrier_id}`);
};

const apiUpdateCarrier = (carrier_id: number, data: any) => {
  return PATCH(`api/carriers/${carrier_id}`, data);
};

const apiGetActurisProductsByType = () => {
  return GET('api/carriers/acturis-products-by-type');
};

const apiCreateApplicationTextValidatorLog = (uuid: string, data: any) => {
  return POST(`api/policy/mc/${uuid}/application/text-validator-log`, data);
};

const apiApplicationTextValidatorLog = (uuid: string, id: number) => {
  return GET(`api/policy/mc/${uuid}/application/text-validator-log/${id}`);
};

const apiCreateSubmission = (data: { lead_id: number; type: number }) => {
  return POST('api/submissions/', data);
};

const apiGetSubmissions = (page: number, query: string) => {
  const params = page > 0 ? `?page=${page}&${query}` : `?${query}`;
  return GET(`api/submissions/${params}`);
};

const apiGetSubmission = (submission_id: number) => {
  return GET(`api/submissions/${submission_id}/`);
};

const apiGetLeadSubmissions = (lead_id: number) => {
  return GET(`api/submissions/lead/${lead_id}/`);
};

const apiRemoveSubmissionsPolicy = (uuid: string) => {
  return DELETE(`api/submissions/policy/${uuid}/remove/`);
};

const apiLinkBinders = (policyUuids: string[], policyGroupId?: string) => {
  return POST(`api/submissions/policy-groups/link/`, {
    policy_uuids: policyUuids,
    ...(policyGroupId && { policy_group_id: policyGroupId }),
  });
};

const apiUnlinkBinder = (policyUuid: string) => {
  return POST(`api/submissions/policy-groups/unlink/`, {
    policy_uuid: policyUuid,
  });
};

const apiGetPolicyGroupStatus = (policyGroupId: string) => {
  return GET(`api/submissions/policy-groups/${policyGroupId}/status/`);
};

const apiGetGroupedBillingSync = (policyUuid: string) => {
  return GET(`api/policy/mc/${policyUuid}/grouped-billing-sync`);
};

const apiSetGroupedBillingSource = (
  policyUuid: string,
  sourcePolicyUuid: string | null,
) => {
  return POST(`api/policy/mc/${policyUuid}/grouped-billing-sync`, {
    source_policy_uuid: sourcePolicyUuid,
  });
};

const apiGenerateDashReport = async (data: any) => {
  const userToken = Storage.get('user')?.token;
  const endpoint = Storage.get('API_ENDPOINT');

  try {
    const response = await axios.post(`${endpoint}api/dash/generate/`, data, {
      headers: {
        'Content-Type': 'application/json',
        User: userToken,
      },
      responseType: 'blob', // Expect binary data (PDF or JSON)
    });

    // Check if response is a PDF (binary) or JSON
    const contentType = response.headers['content-type'];

    if (contentType?.includes('application/pdf')) {
      // It's a PDF file - trigger download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);

      // Generate filename on frontend
      const dateStr = new Date().toISOString().split('T')[0];
      const provinceDisplay = data.province;
      const licenseNumber = data.license_number;
      const filename = `DASH Report - ${licenseNumber} (${provinceDisplay}) ${dateStr}`;

      // Create download link and trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { success: true, pdf_downloaded: true };
    } else {
      // It's JSON response - parse and return
      const text = await response.data.text();
      return JSON.parse(text);
    }
  } catch (error: any) {
    // Handle errors
    if (error.response?.data) {
      try {
        const text = await error.response.data.text();
        const errorData = JSON.parse(text);
        throw new Error(errorData.error || 'DASH API request failed');
      } catch {
        throw new Error('DASH API request failed');
      }
    }
    throw error;
  }
};

const apiGenerateLeadDashReport = (leadId: number, data: any) => {
  return POST(`api/dash/generate/`, { ...data, lead_id: leadId });
};
const apiGeneratePolicyDashReport = (policyUuid: string, data: any) => {
  return POST(`api/dash/generate/`, { ...data, policy_uuid: policyUuid });
};

const apiGetProducerCodes = (page: number, query: string = '') => {
  const params = page > 0 ? `?page=${page}&${query}` : `?${query}`;
  return GET(`api/producer_codes/${params}`);
};

const apiGetProducerCode = (producer_code_id: number) => {
  return GET(`api/producer_codes/${producer_code_id}`);
};

const apiCreateProducerCode = (data: any) => {
  return POST('api/producer_codes/', data);
};

const apiUpdateProducerCode = (id: number, data: any) => {
  return PATCH(`api/producer_codes/${id}`, data);
};

const apiGetMyProducerCodes = (page: number, query: string = '') => {
  const params = page > 0 ? `?page=${page}&${query}` : `?${query}`;
  return GET(`api/producer_codes/me/${params}`);
};

const apiSendTokenXCreditCardEmail = (
  uuid: string,
  data: {
    type: 'credit_card' | 'eft';
    section: 'payment_method' | 'down_payment';
  },
) => {
  return POST(`api/policy/mc/${uuid}/application/billing`, data);
};

const apiGetApplicationPayments = (uuid: string) => {
  return GET(`api/policy/mc/${uuid}/application/payments`);
};

const apiCreateBinderPDF = (uuid: string) => {
  return POST(`api/policy/mc/${uuid}/application/binder`, {});
};

const apiGetBinderPDF = (uuid: string, id: number) => {
  return GET(`api/policy/mc/${uuid}/application/binder/${id}`);
};

const apiGetGroupedBinderStatus = (uuid: string) => {
  return GET(`api/policy/mc/${uuid}/application/binder/grouped-status`);
};

const apiCreateESigning = (uuid: string, binder_id: number) => {
  return POST(
    `api/policy/mc/${uuid}/application/binder/${binder_id}/sign`,
    null,
  );
};

const apiGetESigning = (uuid: string, signing_id: number) => {
  return GET(`api/policy/mc/${uuid}/application/signing/${signing_id}`);
};

const apiResetSigning = (uuid: string) => {
  return POST(`api/policy/mc/${uuid}/application/signing/reset`, null);
};

const apiUploadWetSignedPDF = (
  uuid: string,
  binder_id: number,
  data: FormData,
) => {
  return POST(
    `api/policy/mc/${uuid}/application/binder/${binder_id}/wet-sign`,
    data,
  );
};

const apiCancelESigning = (uuid: string, signing_id: number) => {
  return POST(
    `api/policy/mc/${uuid}/application/signing/${signing_id}/cancel`,
    null,
  );
};

const apiSendESigningReminder = (uuid: string, signing_id: number) => {
  return POST(
    `api/policy/mc/${uuid}/application/signing/${signing_id}/reminder`,
    null,
  );
};

const apiGetPolicyPayments = (uuid: string) => {
  return GET(`api/policy/mc/${uuid}/application/payments/overview`);
};

// Specialty Form APIs
const apiGetSpecialtyFormData = (uuid: string) => {
  return GET(`api/policy/mc/${uuid}/specialty/form`);
};

const apiSaveSpecialtyForm = (uuid: string, data: APISpecialtyForm) => {
  return POST(`api/policy/mc/${uuid}/specialty/form`, data);
};

const apiSubmitSpecialtyForm = (uuid: string) => {
  return POST(`api/policy/mc/${uuid}/specialty/form/submit`, {});
};

const apiUploadSpecialtyFile = (uuid: string, file: File, fileType: string) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('file_type', fileType);

  return POST(`api/policy/mc/${uuid}/specialty/files`, formData);
};

const apiGetSpecialtyFiles = (uuid: string) => {
  return GET(`api/policy/mc/${uuid}/specialty/files/list`);
};

const apiDeleteSpecialtyFile = (uuid: string, fileId: number) => {
  return DELETE(`api/policy/mc/${uuid}/specialty/files/${fileId}`);
};

// Commercial Form API functions
const apiGetCommercialFormData = (uuid: string) => {
  return GET(`api/policy/mc/${uuid}/commercial/form`);
};

const apiSaveCommercialForm = (uuid: string, data: any) => {
  return POST(`api/policy/mc/${uuid}/commercial/form`, data);
};

const apiSubmitCommercialForm = (uuid: string) => {
  return POST(`api/policy/mc/${uuid}/commercial/form/submit`, {});
};

const apiGetCommercialFiles = (uuid: string) => {
  return GET(`api/policy/mc/${uuid}/commercial/files/list`);
};

const apiUploadCommercialFile = (
  uuid: string,
  file: File,
  fileType: string,
) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('file_type', fileType);

  return POST(`api/policy/mc/${uuid}/commercial/files`, formData);
};

const apiDeleteCommercialFile = (uuid: string, fileId: number) => {
  return DELETE(`api/policy/mc/${uuid}/commercial/files/${fileId}`);
};

const apiCreateLegacyAutoPolicy = (submissionId: number) => {
  const formData = new FormData();
  formData.append('type', VALIDATION_TYPE_LEGACY_AUTO);
  formData.append('submission_id', submissionId.toString());
  return POST('api/policy/upload', formData);
};

const apiGetLegacyAutoFormData = (uuid: string) => {
  return GET(`api/policy/mc/${uuid}/legacy-auto/form`);
};

const apiSaveLegacyAutoForm = (uuid: string, data: any) => {
  return POST(`api/policy/mc/${uuid}/legacy-auto/form`, data);
};

const apiSubmitLegacyAutoForm = (uuid: string) => {
  return POST(`api/policy/mc/${uuid}/legacy-auto/form/submit`, {});
};

// Life Insurance Form APIs
const apiGetLifeInsuranceFormData = (uuid: string) => {
  return GET(`api/policy/mc/${uuid}/life/form`);
};

const apiSaveLifeInsuranceForm = (uuid: string, data: APILifeInsuranceForm) => {
  return POST(`api/policy/mc/${uuid}/life/form`, data);
};

const apiSubmitLifeInsuranceForm = (uuid: string) => {
  return POST(`api/policy/mc/${uuid}/life/form/submit`, {});
};

const apiUploadLifeFile = (uuid: string, file: File, fileType: string) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('file_type', fileType);

  return POST(`api/policy/mc/${uuid}/life/files`, formData);
};

const apiGetLifeFiles = (uuid: string) => {
  return GET(`api/policy/mc/${uuid}/life/files/list`);
};

const apiDeleteLifeFile = (uuid: string, fileId: number) => {
  return DELETE(`api/policy/mc/${uuid}/life/files/${fileId}`);
};

const apiGetWIPList = (
  page: number = 1,
  filters?: {
    wip_status?: number;
    work_type?: number;
    submission_type?: number;
    carrier?: string;
    client_name?: string;
    sort_field?: string;
    sort_direction?: 'asc' | 'desc';
    view_as_email?: string;
    view_as_branch_id?: string;
  },
) => {
  const params = new URLSearchParams();
  params.append('page', page.toString());

  if (filters) {
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        params.append(key, value.toString());
      }
    });
  }

  const url = `api/submissions/wip/?${params.toString()}`;

  return GET(url);
};

const apiArchiveWIPPolicies = (policyUuids: string[]) => {
  return POST('api/submissions/wip/archive/', { policy_uuids: policyUuids });
};

const apiUploadAppealFiles = (uuid: string, files: File[]) => {
  const formData = new FormData();
  files.forEach((file) => {
    formData.append('files', file);
  });

  return POST(`api/policy/mc/${uuid}/application/appeal-files`, formData);
};

const apiDeleteAppealFile = (uuid: string, fileId: number) => {
  return DELETE(`api/policy/mc/${uuid}/application/appeal-files/${fileId}`);
};

const apiUploadBinderAdditionalFiles = (
  uuid: string,
  files: File[],
  fileType?: string,
) => {
  const formData = new FormData();
  files.forEach((file) => {
    formData.append('files', file);
  });
  if (fileType) {
    formData.append('file_type', fileType);
  }

  return POST(`api/policy/mc/${uuid}/application/additional-files`, formData);
};

const apiDeleteBinderAdditionalFile = (
  uuid: string,
  fileId: number,
  fileType?: string,
) => {
  const url = fileType
    ? `api/policy/mc/${uuid}/application/additional-files/${fileId}?file_type=${fileType}`
    : `api/policy/mc/${uuid}/application/additional-files/${fileId}`;
  return DELETE(url);
};

const apiAppealToUnderwriting = (
  uuid: string,
  data: {
    validations: string[];
    remarks: string[];
    files: string[];
    comments: string;
    supporting_files?: number[];
  },
) => {
  return POST(`api/policy/mc/${uuid}/application/appeal-to-underwriting`, data);
};

const apiGetAppealStatus = (uuid: string, appealLogId: number) => {
  return GET(`api/policy/mc/${uuid}/application/appeal-status/${appealLogId}`);
};

const apiGetBinderList = (
  page: number = 1,
  filters?: {
    wip_status?: number;
    work_type?: number;
    submission_type?: number;
    carrier?: string;
    client_name?: string;
    start_date?: string;
    end_date?: string;
    view_as_email?: string;
    view_as_branch_id?: string;
  },
) => {
  const params = new URLSearchParams();
  params.append('page', page.toString());

  if (filters) {
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        params.append(key, value.toString());
      }
    });
  }

  const url = `api/submissions/binder/?${params.toString()}`;

  return GET(url);
};

const apiGetCustomerList = (
  page: number = 1,
  filters?: {
    search?: string;
    phone?: string;
    email?: string;
    sort_field?: string;
    sort_direction?: 'asc' | 'desc';
  },
) => {
  const params = new URLSearchParams();
  params.append('page', page.toString());

  if (filters) {
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        params.append(key, value.toString());
      }
    });
  }

  const url = `api/customers/?${params.toString()}`;

  return GET(url);
};

const apiGetCustomerOverview = (
  id: number,
  wipPage?: number,
  policiesPage?: number,
  activitiesPage?: number,
) => {
  const params = new URLSearchParams();

  if (wipPage) {
    params.append('wip_page', wipPage.toString());
  }

  if (policiesPage) {
    params.append('policies_page', policiesPage.toString());
  }

  if (activitiesPage) {
    params.append('activities_page', activitiesPage.toString());
  }

  const queryString = params.toString();
  const url = `api/customers/${id}/overview${
    queryString ? `?${queryString}` : ''
  }`;

  return GET(url);
};

const apiAddCustomerRelationship = (
  customerId: number,
  relatedCustomerId: number,
  relationshipType: string,
) => {
  return POST(`api/customers/${customerId}/relationships`, {
    related_customer_id: relatedCustomerId,
    relationship_type: relationshipType,
  });
};

const apiGetSupplementsList = (company: string) => {
  return GET(`api/supplements/list/?company=${encodeURIComponent(company)}`);
};

const apiDownloadSupplement = async (supplementId: string) => {
  const userToken = Storage.get('user')?.token;
  const endpoint = Storage.get('API_ENDPOINT');

  try {
    const response = await axios.get(
      `${endpoint}api/supplements/download/${supplementId}`,
      {
        headers: {
          User: userToken,
        },
        responseType: 'blob',
      },
    );

    // Create blob and download
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);

    // Get filename from response headers or use default
    const contentDisposition = response.headers['content-disposition'];
    let filename = `supplement_${supplementId}.pdf`;
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="(.+)"/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }

    // Create download link and trigger download
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    return { success: true };
  } catch (error) {
    throw error;
  }
};

const apiUploadSupplementFile = (uuid: string, data: FormData) => {
  return POST(`api/policy/mc/${uuid}/application/supplement-files`, data);
};

const apiDeleteSupplementFile = (uuid: string, supplementId: number) => {
  return DELETE(
    `api/policy/mc/${uuid}/application/supplement-files/${supplementId}`,
  );
};

const apiGetHomepageBanners = () => {
  return GET('api/dashboard/homepage/banners/');
};

const apiGetQuickLinks = () => {
  return GET('api/dashboard/homepage/quick-links/');
};

// ── Admin banner CRUD ──────────────────────────────────────────────────────
const apiAdminGetBanners = () => GET('api/dashboard/homepage/banners/admin/');

const apiAdminCreateBanner = (formData: FormData) =>
  POST('api/dashboard/homepage/banners/admin/', formData);

const apiAdminUpdateBanner = (id: number, formData: FormData) =>
  PATCH(`api/dashboard/homepage/banners/admin/${id}/`, formData);

const apiAdminDeleteBanner = (id: number) =>
  DELETE(`api/dashboard/homepage/banners/admin/${id}/`);

const apiAdminReorderBanners = (items: { id: number; order: number }[]) =>
  POST('api/dashboard/homepage/banners/admin/reorder/', items);

// ── Admin Badge CRUD ──────────────────────────────────────────────────────────
const apiAdminGetBadges = () => GET('api/organization/badges/');
const apiAdminCreateBadge = (fd: FormData) =>
  POST('api/organization/badges/', fd);
const apiAdminUpdateBadge = (id: number, fd: FormData) =>
  PATCH(`api/organization/badges/${id}/`, fd);
const apiAdminDeleteBadge = (id: number) =>
  DELETE(`api/organization/badges/${id}/`);
const apiAdminReorderBadges = (items: { id: number; order: number }[]) =>
  POST('api/organization/badges/reorder/', items);
const apiAdminAssignBadge = (userId: number, badgeId: number) =>
  POST(`api/organization/users/${userId}/badges/${badgeId}/`, {});
const apiAdminRemoveBadge = (userId: number, badgeId: number) =>
  DELETE(`api/organization/users/${userId}/badges/${badgeId}/`);

const apiAdminGetQuickLinks = () =>
  GET('api/dashboard/homepage/quick-links/admin/');
const apiAdminCreateQuickLink = (data: object) =>
  POST('api/dashboard/homepage/quick-links/admin/', data);
const apiAdminUpdateQuickLink = (id: number, data: object) =>
  PATCH(`api/dashboard/homepage/quick-links/admin/${id}/`, data);
const apiAdminDeleteQuickLink = (id: number) =>
  DELETE(`api/dashboard/homepage/quick-links/admin/${id}/`);
const apiAdminReorderQuickLinks = (items: { id: number; order: number }[]) =>
  POST('api/dashboard/homepage/quick-links/admin/reorder/', items);

// ── View-As ───────────────────────────────────────────────────────────────────
export type ViewAsState =
  | { type: 'self' }
  | {
      type: 'broker';
      email: string;
      name: string;
      branchId?: number;
      branchName?: string;
    }
  | { type: 'branch'; branchId: number; branchName: string };

export function buildViewAsQS(viewAs?: ViewAsState): string {
  if (!viewAs || viewAs.type === 'self') return '';
  if (viewAs.type === 'broker')
    return `?view_as_email=${encodeURIComponent(viewAs.email)}`;
  if (viewAs.type === 'branch') return `?view_as_branch_id=${viewAs.branchId}`;
  return '';
}

const apiGetViewAsOptions = () => GET('api/dashboard/view-as-options/');
const apiGetViewAsUsersByBranch = (branchId: number) =>
  GET(`api/dashboard/view-as-users/?branch_id=${branchId}`);
const apiGetViewAsUsersBySearch = (search: string) =>
  GET(`api/dashboard/view-as-users/?search=${encodeURIComponent(search)}`);

const apiGetDashboardStatus = (viewAs?: ViewAsState) =>
  GET(`api/dashboard/status${buildViewAsQS(viewAs)}`);

const apiGetDashboardMetrics = (viewAs?: ViewAsState | boolean) => {
  // Legacy boolean support (mp_view=true) kept for safety during transition
  if (typeof viewAs === 'boolean') {
    return GET(`api/dashboard/metrics${viewAs ? '?mp_view=true' : ''}`);
  }
  return GET(`api/dashboard/metrics${buildViewAsQS(viewAs)}`);
};

const apiGetSalesPerformance = (viewAs?: ViewAsState) =>
  GET(`api/analytics/sales-performance${buildViewAsQS(viewAs)}`);

const apiSendCOIToBroker = (uuid: string) => {
  return POST(`api/policy/mc/${uuid}/application/coi/send-to-broker`, {});
};

const apiGetFeatureFlags = () => {
  return GET('api/feature-flags/');
};

const apiGetLifeProducts = () => {
  return GET('api/policy/life-products');
};

// BIG Communications / Notifications APIs
const apiCreateNotification = (data: any, files?: File[]) => {
  const formData = new FormData();

  // Convert arrays to JSON strings for proper handling
  const processedData = { ...data };
  if (processedData.target_branches)
    processedData.target_branches = JSON.stringify(
      processedData.target_branches,
    );
  if (processedData.target_provinces)
    processedData.target_provinces = JSON.stringify(
      processedData.target_provinces,
    );
  if (processedData.target_roles)
    processedData.target_roles = JSON.stringify(processedData.target_roles);

  // Append all text fields
  Object.keys(processedData).forEach((key) => {
    if (
      processedData[key] !== null &&
      processedData[key] !== undefined &&
      processedData[key] !== ''
    ) {
      formData.append(key, processedData[key]);
    }
  });

  // Append files if provided
  if (files && files.length > 0) {
    files.forEach((file) => formData.append('attachments', file));
  }

  return POST('api/notifications/admin/create/', formData);
};

const apiEditNotification = (id: number, data: any, files?: File[]) => {
  const formData = new FormData();

  // Convert arrays to JSON strings for proper handling
  const processedData = { ...data };
  if (processedData.target_branches)
    processedData.target_branches = JSON.stringify(
      processedData.target_branches,
    );
  if (processedData.target_provinces)
    processedData.target_provinces = JSON.stringify(
      processedData.target_provinces,
    );
  if (processedData.target_roles)
    processedData.target_roles = JSON.stringify(processedData.target_roles);

  // Append all text fields
  Object.keys(processedData).forEach((key) => {
    if (
      processedData[key] !== null &&
      processedData[key] !== undefined &&
      processedData[key] !== ''
    ) {
      formData.append(key, processedData[key]);
    }
  });

  // Append files if provided
  if (files && files.length > 0) {
    files.forEach((file) => formData.append('attachments', file));
  }

  return PATCH(`api/notifications/admin/${id}/edit/`, formData);
};

const apiFetchNotifications = (
  page: number,
  status?: string,
  pageSize?: number,
  category?: string,
  search?: string,
  dateFrom?: string,
  dateTo?: string,
) => {
  const params = [
    status ? `status=${status}` : '',
    page > 0 ? `page=${page}` : '',
    pageSize ? `page_size=${pageSize}` : '',
    category ? `category=${encodeURIComponent(category)}` : '',
    search ? `search=${encodeURIComponent(search)}` : '',
    dateFrom ? `date_from=${dateFrom}` : '',
    dateTo ? `date_to=${dateTo}` : '',
  ]
    .filter(Boolean)
    .join('&');
  return GET(`api/notifications/fetch/${params ? `?${params}` : ''}`);
};

// Available categories — kept in sync with backend Notification.CATEGORY_CHOICES
export const NOTIFICATION_CATEGORIES = [
  { value: 'technology', label: 'Technology' },
  { value: 'underwriting', label: 'Underwriting' },
  { value: 'people_and_culture', label: 'People and Culture' },
  { value: 'finance', label: 'Finance' },
  { value: 'commercial_lines', label: 'Commercial Lines' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'general', label: 'General' },
];

const apiMarkNotificationRead = (id: number) => {
  return POST(`api/notifications/${id}/mark-read/`, {});
};

const apiAcknowledgeNotification = (id: number) => {
  return POST(`api/notifications/${id}/acknowledge/`, {});
};

const apiGetAllNotificationsAdmin = (page: number, all?: boolean) => {
  const pageParam = page > 0 ? `page=${page}` : '';
  const allParam = all ? 'all=true' : '';
  const params = [pageParam, allParam].filter(Boolean).join('&');
  return GET(`api/notifications/admin/list/${params ? `?${params}` : ''}`);
};

const apiCalculateRecipients = (data: {
  target_branches?: number[];
  target_provinces?: number[];
  target_roles?: number[];
  target_corporate_leadership?: boolean;
}) => {
  return POST('api/notifications/admin/calculate-recipients/', data);
};

const apiGetNotificationMetadata = () => {
  return GET('api/notifications/metadata/');
};

const apiCalculateInterest = (data: {
  lob: 'auto' | 'home';
  carrier_name: string;
  payment_schedule: string;
  premium: string;
}): Promise<{ interest: string; interest_amount: string }> => {
  return POST('api/policy/mc/calculate-interest', data);
};

// ── BARA (Branch After-Hours Referral Authority) ──────────────────────────────

const apiGetBaraEligibility = (uuid: string) =>
  GET(`api/policy/mc/${uuid}/application/refer/eligibility`);

const apiSubmitBaraReferralToMP = (
  uuid: string,
  data: {
    validations: Array<{ id: string; message: string } | string>;
    remarks: string[];
    files: string[];
    comments: string;
  },
) =>
  POST(`api/policy/mc/${uuid}/application/refer`, {
    route: 'mp_delegate',
    ...data,
  });

const apiGetActivePolicyReferral = (uuid: string) =>
  GET(`api/policy/mc/${uuid}/application/bara-referral`);

const apiGetBaraUserCapabilities = () => GET('api/me/bara-capabilities');

const apiGetBaraReferrals = (
  params?: Record<string, string>,
): Promise<APIBaraReferralPage | APIBaraReferral[]> => {
  const qs = params ? '?' + new URLSearchParams(params).toString() : '';
  return GET(`api/bara/referrals${qs}`);
};

const apiGetBaraReferral = (referralId: string) =>
  GET(`api/bara/referrals/${referralId}`);

const apiAssignBaraReferral = (referralId: string) =>
  POST(`api/bara/referrals/${referralId}/assign`, {});

const apiRecallBaraReferral = (
  referralId: string,
  data?: { reroute_to?: string },
) => POST(`api/bara/referrals/${referralId}/recall`, data ?? {});

const apiApproveBaraReferral = (referralId: string, justification: string) =>
  POST(`api/bara/referrals/${referralId}/approve`, { justification });

const apiDeclineBaraReferral = (referralId: string, justification: string) =>
  POST(`api/bara/referrals/${referralId}/decline`, { justification });

const apiUploadBaraFiles = (uuid: string, files: File[]) => {
  const formData = new FormData();
  files.forEach((file) => {
    formData.append('files', file);
  });
  return POST(`api/policy/mc/${uuid}/application/bara-files`, formData);
};

const apiDeleteBaraFile = (uuid: string, fileId: number) =>
  DELETE(`api/policy/mc/${uuid}/application/bara-files/${fileId}`);

const apiGetBaraComments = (referralId: string) =>
  GET(`api/bara/referrals/${referralId}/comments`);

const apiAddBaraComment = (referralId: string, text: string) =>
  POST(`api/bara/referrals/${referralId}/comments`, { text });

const apiToggleBaraSio = (referralId: string, is_sio: boolean) =>
  POST(`api/bara/referrals/${referralId}/sio`, { is_sio });

const apiRequestBaraInfo = (
  referralId: string,
  data: { message: string; requested_docs?: string },
) => POST(`api/bara/referrals/${referralId}/request-info`, data);

const apiResumeBaraReview = (referralId: string) =>
  POST(`api/bara/referrals/${referralId}/resume-review`, {});

const apiBaraPostBindReview = (
  referralId: string,
  data: { is_sio: boolean; post_bind_review_notes?: string },
) => POST(`api/bara/referrals/${referralId}/post-bind-review`, data);

const apiGetBaraPostBindContext = (referralId: string) =>
  GET(`api/bara/referrals/${referralId}/post-bind-context`);

const apiGetBaraReport = (params?: Record<string, string>) => {
  const qs = params ? '?' + new URLSearchParams(params).toString() : '';
  return GET(`api/bara/report${qs}`);
};

const apiDownloadBaraReportCsv = async (params?: Record<string, string>) => {
  const userToken = Storage.get('user')?.token;
  const endpoint = Storage.get('API_ENDPOINT');
  const qs = params ? '?' + new URLSearchParams(params).toString() : '';

  const response = await axios.get(`${endpoint}api/bara/report/export${qs}`, {
    headers: {
      User: userToken,
    },
    responseType: 'blob',
  });

  const blob = new Blob([response.data], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);

  const timestamp = new Date()
    .toLocaleString('sv-SE', { timeZone: 'America/Toronto' })
    .replace(/:/g, '-');
  const filename = `Referral Report - ${timestamp}.csv`;

  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const apiGetBaraAccessibleBranches = (params?: {
  page?: number;
  page_size?: number;
  name?: string;
  has_referrals?: boolean;
  all?: boolean;
}) => {
  const qs = new URLSearchParams();
  if (params?.page) qs.set('page', String(params.page));
  if (params?.page_size) qs.set('page_size', String(params.page_size));
  if (params?.name) qs.set('name', params.name);
  if (params?.has_referrals) qs.set('has_referrals', 'true');
  if (params?.all) qs.set('all', '');
  const query = qs.toString();
  return GET(`api/bara/branches${query ? `?${query}` : ''}`);
};

const apiGetBranchMembers = (
  branchId: number,
  params?: { role?: number; search?: string },
) => {
  const qs = new URLSearchParams();
  if (params?.role) qs.set('role', String(params.role));
  if (params?.search) qs.set('search', params.search);
  const query = qs.toString();
  return GET(
    `api/bara/branches/${branchId}/members${query ? `?${query}` : ''}`,
  );
};

const apiListBranchHolidays = (branchId: number) =>
  GET(`api/bara/branches/${branchId}/holidays`);

const apiAddBranchHoliday = (
  branchId: number,
  data: { date: string; name: string },
) => POST(`api/bara/branches/${branchId}/holidays`, data);

const apiDeleteBranchHoliday = (branchId: number, holidayId: number) =>
  DELETE(`api/bara/branches/${branchId}/holidays/${holidayId}`);

const apiListBranchAuthority = (branchId: number) =>
  GET(`api/bara/branches/${branchId}/authority`);

const apiGrantBranchAuthority = (branchId: number, data: { user_id: number }) =>
  POST(`api/bara/branches/${branchId}/authority`, data);

const apiRevokeBranchAuthority = (branchId: number, authorityId: number) =>
  DELETE(`api/bara/branches/${branchId}/authority/${authorityId}`);

export {
  apiAuthenticate,
  apiSignOut,
  apiApplicationFormData,
  apiCreateApplicationLog,
  apiValidations,
  apiCreateApplicationFile,
  apiUploadFile,
  apiPolicyList,
  apiPolicyOverview,
  apiCreateNewUser,
  apiGetUsers,
  apiGetUser,
  apiUpdateUser,
  apiResyncActurisConfig,
  apiRemoveUser,
  apiAnalytics,
  apiAativeLeadList,
  apiUnassignedLeadList,
  apiLeadOverview,
  apiLeadStatusUpdate,
  apiCreateApplicationFileValidatorLog,
  apiApplicationFileValidatorLog,
  apiBrokerUploadDocument,
  apiPublicUploadDocument,
  apiSendDocumentReminder,
  apiUpdateDocumentStatus,
  apiResolveDocument,
  apiIgnoreDocument,
  apiDeleteUploadedFile,
  apiGetDeferredDocuments,
  apiSearch,
  apiGetPublicDeferredDocuments,
  apiAssignLeadToUser,
  apiLeadFileUpload,
  apiUpdateLead,
  apiCreateLead,
  apiGetLeadSources,
  apiGetUserWorkflows,
  apiGetWorkflowUsers,
  apiGetLeadTags,
  apiGetLeadLogs,
  apiGetBinderReportData,
  apiGetBinderChecklist,
  apiUpdateBinderChecklist,
  apiAllLeadList,
  apiCreateLeadTag,
  apiGetBranches,
  apiGetWorkflows,
  apiGetWorkflowLeadSources,
  apiCreateWorkflow,
  apiUpdateWorkflow,
  apiGetWorkflow,
  apiAddUserToWorkflow,
  apiRemoveUserFromWorkflow,
  apiCreateBranch,
  apiGetBranch,
  apiUpdateBranch,
  apiGetMyBranches,
  apiGetActurisConfigStatus,
  apiResyncBrokerActurisConfig,
  apiGetSupervisorCandidates,
  apiGetLeadTag,
  apiUpdateLeadTag,
  apiSetOnlineStatus,
  apiGetCallList,
  apiUpdateCallListItem,
  apiExcludeCallListItem,
  apiGetCallListItemLeads,
  apiGetCallListUsers,
  apiBulkAssignCallListItems,
  apiGetCallListAnalytics,
  apiGetSalesAnalytics,
  apiGetAnalyticsCallList,
  apiGetDrillDown,
  apiGetLeadManagement,
  apiGetAnalyticsSubmissions,
  apiGetAnalyticsOverview,
  apiGetLeadHandlers,
  apiGetCarriers,
  apiCreateCarrier,
  apiGetCarrier,
  apiUpdateCarrier,
  apiGetActurisProductsByType,
  apiCreateApplicationTextValidatorLog,
  apiApplicationTextValidatorLog,
  apiCreateSubmission,
  apiGetSubmissions,
  apiGetSubmission,
  apiGetLeadSubmissions,
  apiRemoveSubmissionsPolicy,
  apiLinkBinders,
  apiUnlinkBinder,
  apiGetPolicyGroupStatus,
  apiGetGroupedBillingSync,
  apiSetGroupedBillingSource,
  apiGetProducerCodes,
  apiGetProducerCode,
  apiCreateProducerCode,
  apiUpdateProducerCode,
  apiGetMyProducerCodes,
  apiGenerateDashReport,
  apiGenerateLeadDashReport,
  apiGeneratePolicyDashReport,
  apiSendTokenXCreditCardEmail,
  apiGetApplicationPayments,
  apiCreateBinderPDF,
  apiGetBinderPDF,
  apiGetGroupedBinderStatus,
  apiCreateESigning,
  apiGetESigning,
  apiResetSigning,
  apiUploadWetSignedPDF,
  apiCancelESigning,
  apiSendESigningReminder,
  apiGetPolicyPayments,
  apiGetSpecialtyFormData,
  apiSaveSpecialtyForm,
  apiSubmitSpecialtyForm,
  apiUploadSpecialtyFile,
  apiGetSpecialtyFiles,
  apiDeleteSpecialtyFile,
  apiGetCommercialFormData,
  apiSaveCommercialForm,
  apiSubmitCommercialForm,
  apiGetCommercialFiles,
  apiUploadCommercialFile,
  apiDeleteCommercialFile,
  apiCreateLegacyAutoPolicy,
  apiGetLegacyAutoFormData,
  apiSaveLegacyAutoForm,
  apiSubmitLegacyAutoForm,
  apiGetLifeInsuranceFormData,
  apiSaveLifeInsuranceForm,
  apiSubmitLifeInsuranceForm,
  apiUploadLifeFile,
  apiGetLifeFiles,
  apiDeleteLifeFile,
  apiGetWIPList,
  apiArchiveWIPPolicies,
  apiUploadAppealFiles,
  apiDeleteAppealFile,
  apiUploadBinderAdditionalFiles,
  apiDeleteBinderAdditionalFile,
  apiAppealToUnderwriting,
  apiGetAppealStatus,
  apiGetBinderList,
  apiGetDashboardStatus,
  apiGetDashboardMetrics,
  apiGetSalesPerformance,
  apiGetCustomerList,
  apiGetCustomerOverview,
  apiAddCustomerRelationship,
  apiGetSupplementsList,
  apiDownloadSupplement,
  apiUploadSupplementFile,
  apiDeleteSupplementFile,
  apiSendCOIToBroker,
  apiGetFeatureFlags,
  apiGetLifeProducts,
  apiCreateNotification,
  apiEditNotification,
  apiFetchNotifications,
  apiMarkNotificationRead,
  apiAcknowledgeNotification,
  apiGetAllNotificationsAdmin,
  apiCalculateRecipients,
  apiGetNotificationMetadata,
  apiCalculateInterest,
  apiGetHomepageBanners,
  apiGetQuickLinks,
  apiAdminGetBadges,
  apiAdminCreateBadge,
  apiAdminUpdateBadge,
  apiAdminDeleteBadge,
  apiAdminReorderBadges,
  apiAdminAssignBadge,
  apiAdminRemoveBadge,
  apiAdminGetBanners,
  apiAdminCreateBanner,
  apiAdminUpdateBanner,
  apiAdminDeleteBanner,
  apiAdminReorderBanners,
  apiAdminGetQuickLinks,
  apiAdminCreateQuickLink,
  apiAdminUpdateQuickLink,
  apiAdminDeleteQuickLink,
  apiAdminReorderQuickLinks,
  apiGetViewAsOptions,
  apiGetViewAsUsersByBranch,
  apiGetViewAsUsersBySearch,
  apiGetBaraEligibility,
  apiSubmitBaraReferralToMP,
  apiGetActivePolicyReferral,
  apiGetBaraUserCapabilities,
  apiGetBaraReferrals,
  apiGetBaraReferral,
  apiAssignBaraReferral,
  apiRecallBaraReferral,
  apiApproveBaraReferral,
  apiDeclineBaraReferral,
  apiUploadBaraFiles,
  apiDeleteBaraFile,
  apiGetBaraComments,
  apiAddBaraComment,
  apiToggleBaraSio,
  apiRequestBaraInfo,
  apiResumeBaraReview,
  apiBaraPostBindReview,
  apiGetBaraPostBindContext,
  apiGetBaraReport,
  apiDownloadBaraReportCsv,
  apiGetBaraAccessibleBranches,
  apiGetBranchMembers,
  apiListBranchHolidays,
  apiAddBranchHoliday,
  apiDeleteBranchHoliday,
  apiListBranchAuthority,
  apiGrantBranchAuthority,
  apiRevokeBranchAuthority,
};

export * from './api/acturis';
export * from './api/quote-marketplace';
export * from './api/mvr-request';
